import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../supabase';
import ModelViewerCard from '../ThreeD/ModelViewerCard';
import LandingFooter from '../Landing/LandingFooter';
import SEO from '../SEO';
import '../../styles/artgallery.css';

// ── Simple SVG Icon Components ──────────────────────────────────────────────
const GalleryIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="ag-tab-svg">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const StoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="ag-tab-svg">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 01-8 0" />
  </svg>
);

const CubeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="ag-tab-svg">
    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '18px', height: '18px' }}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ZoomIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '24px', height: '24px' }}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
    <line x1="11" y1="8" x2="11" y2="14" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px', display: 'inline', marginRight: '6px' }}>
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const PaletteIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '48px', height: '48px', color: 'var(--ag-muted)', margin: '0 auto 20px', display: 'block' }}>
    <circle cx="13.5" cy="6.5" r="0.5" fill="currentColor" />
    <circle cx="17.5" cy="10.5" r="0.5" fill="currentColor" />
    <circle cx="8.5" cy="7.5" r="0.5" fill="currentColor" />
    <circle cx="6.5" cy="12.5" r="0.5" fill="currentColor" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125C12.99 18.837 13 18.21 13 18c0-.926.746-1.648 1.648-1.648H17c2.761 0 5-2.239 5-5C22 6.477 17.523 2 12 2z" />
  </svg>
);

// ── 3D Models ────────────────────────────────────────────────────────────────
const MY_MODELS = [
  { id: 1, name: 'Clip Stand Design', file: process.env.PUBLIC_URL + '/models/round_clip_stand.glb', desc: 'Modern furniture design' },
  { id: 2, name: 'Frequency Light Sensor', file: process.env.PUBLIC_URL + '/models/circuit_1.glb', desc: 'Electronic sensor visualization' },
  { id: 3, name: 'ELIOT Stand', file: process.env.PUBLIC_URL + '/models/eliot_stand 3 sidde v1.glb', desc: 'Retail display stand — Emmanuels Lanka' },
  { id: 4, name: 'TFL Branding', file: process.env.PUBLIC_URL + '/models/ELIOT TFL.glb', desc: 'Branding concept — Emmanuels Lanka' },
];

// ── WhatsApp helper ──────────────────────────────────────────────────────────
const openWhatsApp = (msg = '') => {
  const phone = '94770311025';
  const text = msg ? `Hi, I would like to order/discuss: ${msg}` : `Hi, I'm interested in your art.`;
  window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank');
};

// ── Lightbox ─────────────────────────────────────────────────────────────────
const Lightbox = ({ image, onClose }) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      // Block Ctrl+S / Ctrl+U / Ctrl+P / Ctrl+A while lightbox is open
      if ((e.ctrlKey || e.metaKey) && ['s', 'u', 'p', 'a'].includes(e.key.toLowerCase())) {
        e.preventDefault();
      }
    };
    // Block right-click at the document level while lightbox is open
    const blockContext = (e) => e.preventDefault();
    document.addEventListener('keydown', handleKey);
    document.addEventListener('contextmenu', blockContext);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('contextmenu', blockContext);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="ag-lightbox" onClick={onClose}>
      <button className="ag-lightbox-close" onClick={onClose} aria-label="Close">
        <CloseIcon />
      </button>
      <div
        className="ag-lightbox-inner"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Transparent shield sits on top of the image and intercepts all pointer events */}
        <div className="ag-img-shield" />
        <img
          src={image.url}
          alt="artwork"
          draggable="false"
          onDragStart={(e) => e.preventDefault()}
        />
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
const ArtGallery = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === '3d' ? '3d' : 'gallery';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lightboxImg, setLightboxImg] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Sync active tab with URL param
  const switchTab = (tab) => {
    if (tab === 'store') {
      navigate('/art-portfolio');
      return;
    }
    setActiveTab(tab);
    if (tab === '3d') {
      setSearchParams({ tab: '3d' });
    } else {
      setSearchParams({});
    }
  };

  // Re-sync if URL param changes (e.g. browser back/forward)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === '3d') setActiveTab('3d');
    else if (!tabParam) setActiveTab(prev => prev === '3d' ? 'gallery' : prev);
  }, [searchParams]);

  // Fetch all images from Supabase 'art' bucket
  useEffect(() => {
    if (activeTab !== 'gallery') return;
    const fetchArt = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: listErr } = await supabase.storage.from('art').list('', {
          limit: 500,
          offset: 0,
        });
        if (listErr) throw listErr;

        const imageFiles = (data || []).filter(file =>
          file.name && /\.(jpg|jpeg|png|gif|webp|avif|svg)$/i.test(file.name)
        );

        const images = imageFiles.map(file => {
          const { data: urlData } = supabase.storage.from('art').getPublicUrl(file.name);
          return { name: file.name, url: urlData.publicUrl };
        });

        setArtworks(images);
      } catch (err) {
        console.error('Error fetching art:', err);
        setError('Could not load artworks. Make sure your Supabase "art" bucket is set to Public.');
      }
      setLoading(false);
    };
    fetchArt();
  }, [activeTab]);

  // Handle tab clicks — Art Store navigates away to /art-portfolio
  const handleTabClick = (tab) => switchTab(tab);

  return (
    <div className="ag-page">
      <SEO
        title="Art Gallery & 3D Creations | Namal Chamodya Creative Studio"
        description="Explore Namal Chamodya's creative studio featuring digital art, pencil sketches, graphic designs, and interactive 3D models. Commission custom artwork, social media designs, and branding packages."
        keywords="Namal Chamodya art, digital art, 3D models, pencil sketches, graphic design, creative art, 3D designer, art gallery Sri Lanka, social media designs, branding, nc art, Namal art studio, 3D creations, Blender models"
        canonical="/art"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "NC. Art Studio — Digital Art & 3D Gallery",
          "description": "Digital designs, pencil sketches, and 3D creations by Namal Chamodya.",
          "url": "https://namal-chamodya.space/art",
          "author": {
            "@type": "Person",
            "name": "Namal Chamodya"
          }
        }}
      />

      {/* ── Hero ── */}
      <header className="ag-hero">
        <div className="ag-hero-bg">
          <div className="ag-hero-orb ag-hero-orb-1" />
          <div className="ag-hero-orb ag-hero-orb-2" />
          <div className="ag-hero-orb ag-hero-orb-3" />
        </div>
        <div className="ag-hero-content">
          <span className="ag-hero-label">Creative Studio</span>
          <h1 className="ag-hero-title">
            NC<span className="ag-gold">.</span>ART
          </h1>
          <p className="ag-hero-sub">
            Digital designs, pencil sketches &amp; 3D creations — all in one place.
          </p>
          <div className="ag-hero-actions">
            <button className="ag-btn-primary" onClick={() => openWhatsApp('Art Commission')}>
              Commission Work
            </button>
            <a href="mailto:namalcg12@gmail.com" className="ag-btn-ghost">
              Say Hello
            </a>
          </div>
        </div>
      </header>

      {/* ── Tab Navigation ── */}
      <div className="ag-tabs-wrapper">
        <div className="ag-tabs">
          <button
            className={`ag-tab ${activeTab === 'gallery' ? 'active' : ''}`}
            onClick={() => handleTabClick('gallery')}
          >
            <GalleryIcon />
            Art Gallery
          </button>
          <button
            className="ag-tab"
            onClick={() => handleTabClick('store')}
          >
            <StoreIcon />
            Art Store
          </button>
          <button
            className={`ag-tab ${activeTab === '3d' ? 'active' : ''}`}
            onClick={() => handleTabClick('3d')}
          >
            <CubeIcon />
            3D Gallery
          </button>
        </div>
      </div>

      {/* ── Tab Content ── */}
      <div className="ag-tab-content">

        {/* ─ GALLERY TAB ─ */}
        {activeTab === 'gallery' && (
          <section className="ag-gallery-section">
            <div className="ag-section-head">
              <h2>All <span className="ag-gold">Artworks</span></h2>
            </div>

            {loading && (
              <div className="ag-loading">
                <div className="ag-spinner" />
                <p>Loading artworks from studio...</p>
              </div>
            )}

            {error && (
              <div className="ag-error">
                <p><AlertIcon />{error}</p>
                <p style={{ fontSize: '0.85rem', color: '#555', marginTop: '10px' }}>
                  Make sure your Supabase <code>art</code> bucket is public and contains images.
                </p>
              </div>
            )}

            {!loading && !error && artworks.length === 0 && (
              <div className="ag-empty">
                <PaletteIcon />
                <h3>Gallery Coming Soon</h3>
                <p>Upload your artworks to the <strong>art</strong> bucket in Supabase Storage.</p>
              </div>
            )}

            {!loading && artworks.length > 0 && (
              <div className="ag-masonry">
                {artworks.map((img, idx) => (
                  <div
                    key={img.name + idx}
                    className="ag-masonry-item"
                    onClick={() => setLightboxImg(img)}
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                  >
                    <img
                      src={img.url}
                      alt="artwork"
                      loading="lazy"
                      draggable="false"
                      onContextMenu={(e) => e.preventDefault()}
                    />
                    <div className="ag-masonry-overlay">
                      <span className="ag-zoom-icon"><ZoomIcon /></span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ─ 3D GALLERY TAB ─ */}
        {activeTab === '3d' && (
          <section className="ag-3d-section">
            <div className="ag-section-head">
              <h2>3D <span className="ag-gold">Gallery</span></h2>
              <p>Interactive 3D models — drag to rotate, pinch to zoom</p>
            </div>
            <div className="ag-3d-grid">
              {MY_MODELS.map((model) => (
                <div key={model.id} className="ag-3d-card">
                  <div className="ag-3d-viewer">
                    <ModelViewerCard src={model.file} alt={model.name} />
                  </div>
                  <div className="ag-3d-info">
                    <h3>{model.name}</h3>
                    <p>{model.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

      </div>

      {/* ── Lightbox ── */}
      {lightboxImg && (
        <Lightbox image={lightboxImg} onClose={() => setLightboxImg(null)} />
      )}

      <LandingFooter />
    </div>
  );
};

export default ArtGallery;
